import { createRef, PureComponent } from 'react';
import moment from 'moment';
import { get, isInteger, isEqual } from 'lodash';
import { Form, Ref } from 'semantic-ui-react';
import { DayPicker } from 'react-day-picker';

// COMPONENTS
import Tooltip from 'components/Tooltip';
import { IconButton } from 'components/Buttons';
import FormNumberField from 'componentsV2/Fields/numberField';

// HOCS
import withFormikInput from './withFormikInput';
import withFormField from './withFormField';

// UTILS
import { twoDigitZeroPadTransform, fourDigitZeroPadTransform, fourDigitPadTransform } from './Fields.transforms';

const appRoot = document.getElementById('app');

const dateValidator = (value) => {
  if (isEqual(value, { year: '', month: '', date: '' })) {
    return undefined;
  }

  if (value.year === '' || value.month === '' || value.date === '') {
    return { translationId: 'errors.validation.incompleteDate' };
  }

  if (!isInteger(Number(value.year)) || !isInteger(Number(value.month)) || !isInteger(Number(value.date))) {
    return { translationId: 'errors.validation.onlyNumbers' };
  }

  if (Number(value.month) > 12 || Number(value.month) < 1) {
    return { translationId: 'errors.validation.monthOutOfBounds' };
  }

  if (Number(value.date) > 31 || Number(value.date) < 1) {
    return { translationId: 'errors.validation.dateOutOfBounds' };
  }

  const givenDate = new Date(`${value.year}-${value.month}-${value.date}`); // UTC assumed by date since not specified

  if (
    Number(value.date) !== givenDate.getUTCDate() || // Need to compare UTC time because of earlier statement
    Number(value.month) !== givenDate.getUTCMonth() + 1 || // Month given [0,11]
    Number(value.year) !== givenDate.getUTCFullYear()
  ) {
    return { translationId: 'errors.validation.invalidDate' };
  }

  return undefined;
};

const handleYearChange = (event, onChangleHandler) => {
  const { value } = event.target;
  if (value.length <= 4) {
    onChangleHandler(event);
  }
};

const handleDayMonthChange = (event, onChangleHandler) => {
  const { value } = event.target;
  if (value.length <= 2) {
    onChangleHandler(event);
  }
};

export class DateField extends PureComponent {
  state = {
    hasFocus: false,
    pickerVisible: false,
    parentPosition: {},
  };

  fieldRef = createRef();

  _getFieldValue = (event) => event.target.value;

  onDayClick = (value) => {
    // @ts-expect-error TS(2339): Property 'name' does not exist on type 'Readonly<{... Remove this comment to see the full error message
    const { name, setFieldValue } = this.props;
    const date = moment(value);

    this.setState(
      {
        pickerVisible: false,
      },
      () => {
        setFieldValue(name, {
          [`year`]: fourDigitZeroPadTransform(date.year()),
          [`month`]: twoDigitZeroPadTransform(date.month() + 1),
          [`date`]: twoDigitZeroPadTransform(date.date()),
        });
      }
    );
  };

  togglePicker = () => {
    this.setState((state) => ({
      // @ts-expect-error TS(2339): Property 'pickerVisible' does not exist on type 'R... Remove this comment to see the full error message
      pickerVisible: !state.pickerVisible,
    }));
  };

  hidePicker = () => {
    this.setState({
      pickerVisible: false,
    });
  };

  render() {
    // @ts-expect-error TS(2339): Property 'name' does not exist on type 'Readonly<{... Remove this comment to see the full error message
    const { name, required, setFieldValue, setValues, validators, touched, value, ...rest } = this.props;
    const { pickerVisible } = this.state;

    const pickerDay = moment([Number(value.year), Number(value.month) - 1, Number(value.date)]);
    const pickerMonth = pickerDay.isValid() ? pickerDay.toDate() : new Date();

    return (
      // @ts-expect-error TS(2322): Type 'RefObject<unknown>' is not assign... Remove this comment to see the full error message
      <Ref innerRef={this.fieldRef}>
        <Form.Group widths="equal" inline>
          <FormNumberField
            name={`${name}.year`}
            {...rest}
            // @ts-expect-error TS(2769): No overload matches this call.
            onChange={(event) => handleYearChange(event, rest.onChange)}
            placeholder="yyyy"
            blurTransform={(value) => fourDigitPadTransform(name, value)}
            width={4}
            fluid
          />
          <FormNumberField
            name={`${name}.month`}
            {...rest}
            // @ts-expect-error TS(2769): No overload matches this call.
            onChange={(event) => handleDayMonthChange(event, rest.onChange)}
            placeholder="mm"
            width={4}
            blurTransform={twoDigitZeroPadTransform}
            fluid
          />
          <FormNumberField
            name={`${name}.date`}
            {...rest}
            // @ts-expect-error TS(2769): No overload matches this call.
            onChange={(event) => handleDayMonthChange(event, rest.onChange)}
            blurTransform={twoDigitZeroPadTransform}
            placeholder="dd"
            width={4}
            fluid
          />
          {/* @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message */}
          {!this.props.readOnly && (
            <IconButton
              className="icon-calendar"
              // @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message
              disabled={this.props.readOnly || this.props.disabled}
              onClick={this.togglePicker}
            />
          )}
          {appRoot && (
            // @ts-expect-error TS(2322): Type '{ children: Element; disabled: any; isShown:... Remove this comment to see the full error message
            <Tooltip
              // @ts-expect-error TS(2339): Property 'disabled' does not exist on type 'Readon... Remove this comment to see the full error message
              disabled={this.props.disabled}
              isShown={pickerVisible}
              onOutsideClick={this.hidePicker}
              rootElement={appRoot}
              containerElement={get(this.fieldRef, 'current')}
              width={232}
            >
              <DayPicker
                // @ts-expect-error TS(2769): No overload matches this call.
                disabled={this.props.disabled}
                defaultMonth={pickerMonth}
                onDayClick={this.onDayClick}
              />
            </Tooltip>
          )}
        </Form.Group>
      </Ref>
    );
  }
}

const _DateField = withFormField(DateField);

const FormDateField = withFormikInput(_DateField, {
  validators: [dateValidator],
  passProps: ['setFieldValue', 'touched'],
});

export default FormDateField;
